import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock, Upload } from 'lucide-react';
import { createShareLinks } from '../../config';

/**
 * ClassesTab Component
 * Display classes with category filters, upcoming horizontal scroll, and all classes grid
 * Identical to EventsTab but for classes
 */
const ClassesTab = ({ community, classes }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isInIframe, setIsInIframe] = useState(false);
  const upcomingScrollRef = useRef(null);
  
  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);
  
  const handleClassClick = (classId) => {
    // Always navigate to public class view, ignoring authentication status
    // This ensures public community classes always show the public details page
    navigate(`/group/${community.id}/class/${classId}`);
  };

  // Handle share class
  const handleShareClass = async (e, classId) => {
    e.stopPropagation(); // Prevent card click navigation
    
    const shareUrl = createShareLinks.class(classId);
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      showCopyToast();
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showCopyToast();
      } catch (fallbackErr) {
        console.error('Failed to copy link');
      }
    }
  };

  // Show copy success toast
  const showCopyToast = () => {
    const existingToast = document.querySelector('.copy-toast');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = 'copy-toast';
    toast.textContent = 'Link copied!';
    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast && toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 3000);
  };

  // Format date and time
  const formatClassDateTime = (classItem) => {
    // Handle recurring classes - show pattern regardless of historical date
    if (classItem.is_recurring && classItem.recurrence_pattern) {
      let timeString = '';
      
      // Try to get time from schedule field or date field
      if (classItem.schedule) {
        timeString = classItem.schedule;
      } else if (classItem.date) {
        try {
          const classDate = new Date(classItem.date);
          timeString = classDate.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          });
        } catch (error) {
          timeString = '';
        }
      }
      
      if (classItem.recurrence_pattern === 'daily') {
        return { date: 'Every Day', time: timeString };
      } else if (classItem.recurrence_pattern === 'weekly' && classItem.recurrence_day) {
        const days = {
          'monday': 'Monday',
          'tuesday': 'Tuesday',
          'wednesday': 'Wednesday',
          'thursday': 'Thursday',
          'friday': 'Friday',
          'saturday': 'Saturday',
          'sunday': 'Sunday'
        };
        const dayName = days[classItem.recurrence_day.toLowerCase()] || classItem.recurrence_day;
        return { date: `Every ${dayName}`, time: timeString };
      }
    }
    
    const dateStr = classItem.displayDate || classItem.date || classItem.class_date;
    if (!dateStr) return { date: '', time: '' };
    
    try {
      const classDate = new Date(dateStr);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const classDateOnly = new Date(classDate.getFullYear(), classDate.getMonth(), classDate.getDate());
      
      let dateString = '';
      if (classDateOnly.getTime() === today.getTime()) {
        dateString = 'Today';
      } else if (classDateOnly.getTime() === tomorrow.getTime()) {
        dateString = 'Tomorrow';
      } else {
        dateString = classDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        });
      }
      
      const timeString = classDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      return { date: dateString, time: timeString };
    } catch (error) {
      return { date: '', time: '' };
    }
  };

  // Get unique categories
  const categories = useMemo(() => {
    const uniqueCategories = new Set();
    classes.forEach(classItem => {
      if (classItem.category) {
        uniqueCategories.add(classItem.category);
      }
    });
    return Array.from(uniqueCategories).sort();
  }, [classes]);

  // Filter classes by category
  const filteredClasses = useMemo(() => {
    if (!selectedCategory) return classes;
    
    if (selectedCategory === 'Today') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      
      return classes.filter(classItem => {
        // Check if it's a recurring class that happens today
        if (classItem.is_recurring && classItem.recurrence_pattern) {
          if (classItem.recurrence_pattern === 'daily') {
            return true; // Daily classes always match "Today"
          }
          if (classItem.recurrence_pattern === 'weekly' && classItem.recurrence_day) {
            const dayMap = {
              'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
              'thursday': 4, 'friday': 5, 'saturday': 6
            };
            const recurringDay = dayMap[classItem.recurrence_day.toLowerCase()];
            return recurringDay === dayOfWeek;
          }
        }
        
        // Check regular dated classes
        const dateStr = classItem.displayDate || classItem.date || classItem.class_date;
        if (!dateStr) return false;
        
        try {
          const classDate = new Date(dateStr);
          const classDateOnly = new Date(classDate.getFullYear(), classDate.getMonth(), classDate.getDate());
          return classDateOnly.getTime() >= today.getTime() && classDateOnly.getTime() < tomorrow.getTime();
        } catch (error) {
          return false;
        }
      });
    }
    
    if (selectedCategory === 'This Week') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const sevenDaysFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      const currentDayOfWeek = now.getDay();
      
      return classes.filter(classItem => {
        // Check if it's a recurring class that happens this week
        if (classItem.is_recurring && classItem.recurrence_pattern) {
          if (classItem.recurrence_pattern === 'daily') {
            return true; // Daily classes always match "This Week"
          }
          if (classItem.recurrence_pattern === 'weekly' && classItem.recurrence_day) {
            const dayMap = {
              'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
              'thursday': 4, 'friday': 5, 'saturday': 6
            };
            const recurringDay = dayMap[classItem.recurrence_day.toLowerCase()];
            // Check if this day occurs within the next 7 days
            const daysUntilClass = (recurringDay - currentDayOfWeek + 7) % 7;
            return daysUntilClass < 7;
          }
        }
        
        // Check regular dated classes
        const dateStr = classItem.displayDate || classItem.date || classItem.class_date;
        if (!dateStr) return false;
        
        try {
          const classDate = new Date(dateStr);
          const classDateOnly = new Date(classDate.getFullYear(), classDate.getMonth(), classDate.getDate());
          return classDateOnly.getTime() >= today.getTime() && classDateOnly.getTime() < sevenDaysFromNow.getTime();
        } catch (error) {
          return false;
        }
      });
    }
    
    return classes.filter(classItem => classItem.category === selectedCategory);
  }, [classes, selectedCategory]);

  // Get upcoming classes (first 3 from filtered list)
  const upcomingClasses = useMemo(() => {
    return filteredClasses.slice(0, 3);
  }, [filteredClasses]);

  // Get all classes (excluding the first 3)
  const allClasses = useMemo(() => {
    return filteredClasses.slice(3);
  }, [filteredClasses]);

  const scrollUpcoming = (direction) => {
    if (upcomingScrollRef.current) {
      const scrollAmount = 400;
      const currentScroll = upcomingScrollRef.current.scrollLeft;
      upcomingScrollRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getFilterDisplayName = () => {
    if (selectedCategory === 'Today') return 'today';
    if (selectedCategory === 'This Week') return 'this week';
    if (selectedCategory) return `for ${selectedCategory}`;
    return '';
  };

  const hasAnyClasses = upcomingClasses.length > 0 || allClasses.length > 0;

  // Empty state
  if (!classes || classes.length === 0) {
    return (
      <div className="empty-state">
        No upcoming classes
      </div>
    );
  }

  // Render horizontal class card (upcoming section)
  const renderHorizontalCard = (classItem) => {
    const { date, time } = formatClassDateTime(classItem);
    
    return (
      <div 
        key={classItem.id}
        className="horizontal-event-card"
        onClick={() => handleClassClick(classItem.id)}
      >
        <div className="horizontal-event-image">
          {classItem.cover_image_url && classItem.cover_image_url.trim() !== '' ? (
            <img 
              src={classItem.cover_image_url}
              alt={classItem.title}
              className="event-image"
            />
          ) : (
            <div className="event-gradient-placeholder">
              <span className="event-placeholder-emoji">📚</span>
            </div>
          )}
          <button 
            className="event-share-button"
            onClick={(e) => handleShareClass(e, classItem.id)}
            aria-label="Share class"
          >
            <Upload size={16} />
          </button>
        </div>
        
        <div className="horizontal-event-title">
          {classItem.title}
        </div>
        
        <div className="horizontal-event-info">
          <div className="horizontal-event-left">
            <div className="horizontal-event-row">
              <Calendar size={16} className="event-icon" />
              <span className="horizontal-event-text">{date}</span>
            </div>
            <div className="horizontal-event-row">
              <Clock size={16} className="event-icon" />
              <span className="horizontal-event-text">{time}</span>
            </div>
          </div>
          
          {classItem.teacher && (
            <div className="horizontal-event-price">
              <div className="horizontal-event-rsvp-label">Teacher</div>
              <div className="horizontal-event-price-text">
                {classItem.teacher}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render compact class card (all classes section)
  const renderCompactCard = (classItem) => {
    const { date, time } = formatClassDateTime(classItem);
    
    return (
      <div 
        key={classItem.id}
        className="compact-event-card"
        onClick={() => handleClassClick(classItem.id)}
      >
        <div className="compact-event-image">
          {classItem.cover_image_url && classItem.cover_image_url.trim() !== '' ? (
            <img 
              src={classItem.cover_image_url}
              alt={classItem.title}
              className="event-image"
            />
          ) : (
            <div className="event-gradient-placeholder">
              <span className="event-placeholder-emoji">📚</span>
            </div>
          )}
          <button 
            className="event-share-button"
            onClick={(e) => handleShareClass(e, classItem.id)}
            aria-label="Share class"
          >
            <Upload size={14} />
          </button>
        </div>
        
        <div className="compact-event-content">
          <div className="compact-event-title">
            {classItem.title}
          </div>
          
          <div className="horizontal-event-info">
            <div className="horizontal-event-left">
              <div className="compact-event-row">
                <Calendar size={14} className="event-icon" />
                <span className="compact-event-text">{date}</span>
              </div>
              <div className="compact-event-row">
                <Clock size={14} className="event-icon" />
                <span className="compact-event-text">{time}</span>
              </div>
            </div>
            
            {classItem.teacher && (
              <div className="compact-event-price">
                <span className="compact-rsvp-label">Teacher</span>
                <span className="compact-price-text">
                  {classItem.teacher}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="events-container">
      {/* Categories Filter */}
      <div className="events-categories-section">
        <div 
          className="events-categories-scroll"
          style={{ touchAction: 'pan-x' }}
        >
          <button
            className={`event-category-chip ${!selectedCategory ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            All
          </button>
          
          <button
            className={`event-category-chip ${selectedCategory === 'Today' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('Today')}
          >
            Today
          </button>
          
          <button
            className={`event-category-chip ${selectedCategory === 'This Week' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('This Week')}
          >
            This Week
          </button>
          
          {categories.map((category) => (
            <button
              key={category}
              className={`event-category-chip ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state for filtered results */}
      {selectedCategory && !hasAnyClasses ? (
        <div className="events-empty-state">
          <div className="events-empty-text">
            Nothing happening {getFilterDisplayName()}
          </div>
        </div>
      ) : (
        <>
          {/* Upcoming Classes Section */}
          {upcomingClasses.length > 0 && (
            <div className="upcoming-events-section">
              <div className="events-section-header">
                <h2 className="events-section-title">Upcoming Classes</h2>
                {isInIframe && upcomingClasses.length > 1 && (
                  <div className="upcoming-scroll-arrows" style={{ display: 'flex' }}>
                    <button 
                      className="event-scroll-arrow"
                      onClick={() => scrollUpcoming('left')}
                      aria-label="Scroll left"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button 
                      className="event-scroll-arrow"
                      onClick={() => scrollUpcoming('right')}
                      aria-label="Scroll right"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </div>
              
              <div 
                className="upcoming-events-scroll"
                ref={upcomingScrollRef}
              >
                {upcomingClasses.map(classItem => renderHorizontalCard(classItem))}
              </div>
            </div>
          )}

          {/* All Classes Section */}
          {allClasses.length > 0 && (
            <div className="all-events-section">
              <h2 className="events-section-title">All Classes</h2>
              <div className="all-events-grid">
                {allClasses.map(classItem => renderCompactCard(classItem))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClassesTab;
